import { useState, useEffect, type MouseEvent } from "react";
import {
  Container,
  Grid,
  Typography,
  Button,
  Box,
  Alert,
  MenuItem,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Popover, // <-- add Popover
} from "@mui/material";
import type { Artwork } from "../types/artwork";
import type { SortOption } from "../types/reorder";
import PageContent from "../sections/PageContent";
import ArtworkCard from "../components/cards/ArtworkCard";
import { useSearchParams } from "react-router-dom";
import SortIcon from "@mui/icons-material/Sort";
import api from "../api";
import ArtworkSkeleton from "../components/ArtworkSkeleton";
import { useLanguage } from "../context/LanguageContext";
import { useSiteTheme } from "../theme/ThemeContext";
import SEO from "../components/SEO";

function Artworks() {
  const { language } = useLanguage();
  const [artworks, setArtworks] = useState<Artwork[]>([]);
  const [filteredArtworks, setFilteredArtworks] = useState<Artwork[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [sortBy, setSortBy] = useState<SortOption>("name-asc");
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [sortAnchorEl, setSortAnchorEl] = useState<HTMLElement | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const { palette } = useSiteTheme();

  const handleSortClick = (event: MouseEvent<HTMLElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  useEffect(() => {
    const fetchArtworks = async () => {
      try {
        const response = await api.get<Artwork[]>("/artworks");
        const artworksData = response.data;

        if (!Array.isArray(response.data)) {
          setError("Invalid response from server");
          return;
        }

        setArtworks(response.data);

        setError(null);
      } catch (error) {
        console.log("Artwork error", error);
        setError("Failed to load artworks. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchArtworks();
  }, []);

  // Read URL parameters on component mount
  useEffect(() => {
    const categoryParam = searchParams.get("category");
    const searchParam = searchParams.get("search");

    setSelectedCategory(categoryParam ?? "all");
    setSearchTerm(searchParam ?? "");
  }, [searchParams]);

  // Filter and sort artworks
  useEffect(() => {
    let result = [...artworks];

    // Apply search filter
    if (searchTerm) {
      const searchTermLower = searchTerm.toLowerCase();
      result = result.filter((artwork) =>
        artwork.title[language]?.toLowerCase().includes(searchTermLower),
      );
    }
    // Apply sorting
    result.sort((a, b) => {
      switch (sortBy) {
        case "price-asc":
          return Number(a.price) - Number(b.price);
        case "price-desc":
          return Number(b.price) - Number(a.price);
        case "name-asc":
          return a.title[language].localeCompare(b.title[language]);
        case "name-desc":
          return b.title[language].localeCompare(a.title[language]);
        default:
          return 0;
      }
    });

    setFilteredArtworks(result);
  }, [artworks, selectedCategory, sortBy, searchTerm, language]);

  const updateSearchTerm = (value: string) => {
    setSearchTerm(value);
  };

  const updateSortBy = (value: SortOption) => {
    setSortBy(value);
  };

  if (loading) {
    return (
      <Container sx={{ py: 8 }} maxWidth="lg">
        <Typography variant="h4" component="h1" gutterBottom>
          My Artworks
        </Typography>

        {/* Search and Filter Section Skeleton */}
        <Box sx={{ mb: 4, display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Skeleton variant="rectangular" height={56} sx={{ flexGrow: 1 }} />
          <Skeleton variant="rectangular" height={56} width={200} />
          <Skeleton variant="rectangular" height={56} width={200} />
        </Box>

        {/* Artworks Grid Skeleton */}
        <Grid container spacing={4}>
          {[...Array(6)].map((_, index) => (
            // <Grid key={index} size={{ xs: 12, sm: 6, md: 4 }}>
            <Grid item xs={12} sm={6} md={4}>
              <ArtworkSkeleton />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </Container>
    );
  }

  // Build dynamic title and description based on filters
  const getPageTitle = () => {
    if (searchTerm) {
      return `Search Results for "${searchTerm}"`;
    }
    if (selectedCategory !== "all") {
      return `${selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)}`;
    }
    return "My Artworks";
  };

  const getPageDescription = () => {
    if (searchTerm) {
      return `Search results for "${searchTerm}" at Byzantine Art Studio. Find hand painted iconograpy.`;
    }
    if (selectedCategory !== "all") {
      return `Browse our collection of ${selectedCategory} Icons at Byzantine Art Studio, hand painted iconograpy`;
    }
    return "Browse our complete collection of hand painted Icons at Byzantine Art Studio. Beautiful, unique artwork for your home.";
  };

  return (
    <>
      <SEO
        title={getPageTitle()}
        description={getPageDescription()}
        url="/artworks"
      />
      <PageContent bg={palette.hero}>
        <Container sx={{ py: 8 }} maxWidth="lg">
          {(searchTerm || selectedCategory !== "all") && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" component="h1" gutterBottom>
                {searchTerm
                  ? `Search results for: "${searchTerm}"`
                  : `Category: ${selectedCategory}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {filteredArtworks.length} artwork
                {filteredArtworks.length !== 1 ? "s" : ""} found
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 4,
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ minWidth: { xs: "100%", sm: "auto" } }}
            >
              <TextField
                size="small"
                label={language === "ro" ? "Caută icoane" : "Search icons"}
                placeholder={
                  language === "ro" ? "Sfânt, titlu..." : "Saint, title..."
                }
                value={searchTerm}
                onChange={(event) => updateSearchTerm(event.target.value)}
                sx={{
                  minWidth: { xs: "100%", sm: 260 },
                }}
              />
              {/* Sort Icon Button */}
              <IconButton
                aria-label="Sort"
                onClick={handleSortClick}
                size="large"
              >
                <SortIcon />
                <Typography variant="button" sx={{ ml: 1, fontWeight: 700 }}>
                  Sort
                </Typography>
              </IconButton>
              <Popover
                open={Boolean(sortAnchorEl)}
                anchorEl={sortAnchorEl}
                onClose={handleSortClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              >
                <Box sx={{ p: 1, minWidth: 180 }}>
                  <MenuItem
                    selected={sortBy === "name-asc"}
                    onClick={() => {
                      updateSortBy("name-asc");
                      handleSortClose();
                    }}
                  >
                    Name (A-Z)
                  </MenuItem>
                  <MenuItem
                    selected={sortBy === "name-desc"}
                    onClick={() => {
                      updateSortBy("name-desc");
                      handleSortClose();
                    }}
                  >
                    Name (Z-A)
                  </MenuItem>
                  <MenuItem
                    selected={sortBy === "price-asc"}
                    onClick={() => {
                      updateSortBy("price-asc");
                      handleSortClose();
                    }}
                  >
                    Price (Low to High)
                  </MenuItem>
                  <MenuItem
                    selected={sortBy === "price-desc"}
                    onClick={() => {
                      updateSortBy("price-desc");
                      handleSortClose();
                    }}
                  >
                    Price (High to Low)
                  </MenuItem>
                </Box>
              </Popover>
            </Stack>
          </Box>

          {/* Artworks Grid */}
          <Grid container spacing={4}>
            {filteredArtworks.map((artwork) => (
              // <Grid key={artwork.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <Grid key={artwork.id} item xs={12} sm={6} md={4}>
                {/* <Grid item xs={12} sm={6} md={4}> */}
                <ArtworkCard artwork={artwork} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </PageContent>
    </>
  );
}

export default Artworks;
